// Formular zum Erstellen/Bearbeiten eines Interpreten (Artist, Spotify-Vorbild).
// Create-Modus: Name/Bio/Profilbild → nach dem Anlegen wechselt der Parent in den Edit-Modus.
// Edit-Modus: zusätzlich Publish-Toggle + Löschen. Die Titel↔Interpret-Zuordnung läuft pro Titel
// im Audio-Formular (kein Produkt-Picker hier). Blueprint: AlbumForm.
import React, { useId, useState } from 'react';
import { PhotoIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { artistService } from '../../services/artistService';
import { Artist } from '../../types/artist';
import type { BenachrichtigungsTyp } from '../ui/Benachrichtigung';

interface ArtistFormProps {
  initial: Artist | null; // null = Create-Modus
  onCreated: (a: Artist) => void;
  onClose: () => void;
  onDeleted: (id: string) => void;
  /** Meldet an den Balken der Elternseite. Tonlage aus `BenachrichtigungsTyp` — nicht abschreiben. */
  notify: (type: BenachrichtigungsTyp, message: string) => void;
}

export const ArtistForm: React.FC<ArtistFormProps> = ({ initial, onCreated, onClose, onDeleted, notify }) => {
  const isEdit = !!initial;
  // Eine Wurzel je Formular-Instanz, daraus abgeleitete Feld-Kennungen — Muster aus
  // `ui/Filterleiste.tsx`. Feste Zeichenketten wären beim zweiten Vorkommen im selben Baum
  // (Liste plus Modal, Test mit zwei Formularen) doppelt, und eine doppelte `id` bricht die
  // Verdrahtung still: `htmlFor` findet dann das erste Feld, nicht das eigene.
  const feldId = useId();
  const [ar, setAr] = useState<Artist | null>(initial);
  const [name, setName] = useState(initial?.name ?? '');
  const [bio, setBio] = useState(initial?.bio ?? '');
  const [profileImageUrl, setProfileImageUrl] = useState(initial?.profileImageUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  // Sicherheitsabfragen — Feldname `rückfrage`, nicht `inhalt` (das ist für den
  // Benachrichtigungs-Balken belegt). Warum hinter dem `await` nichts veralten kann, steht
  // ausführlich in `AlbumForm.tsx`.
  const { rückfrage, frage, antworte } = useRueckfrage();

  const handleSaveMeta = async () => {
    if (!name.trim()) {
      notify('error', 'Name ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        bio: bio.trim() || null,
        profileImageUrl: profileImageUrl.trim() || null,
      };
      if (isEdit && ar) {
        const updated = await artistService.updateArtist(ar.id, payload);
        setAr(updated);
        notify('success', 'Gespeichert');
      } else {
        const created = await artistService.createArtist(payload);
        notify('success', 'Interpret erstellt');
        onCreated(created);
      }
    } catch {
      notify('error', 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: 'DRAFT' | 'PUBLISHED') => {
    if (!ar || mutating) return;
    // Ohne `gefahr`: Veröffentlichen zerstört nichts — derselbe Knopf heißt danach
    // „Zurückziehen" und stellt die Sichtbarkeit zurück.
    if (status === 'PUBLISHED' && !ar.profileImageUrl) {
      const trotzdem = await frage({
        titel: 'Interpret ohne Profilbild veröffentlichen?',
        text:
          'Dieser Interpret hat kein Profilbild. Veröffentlicht ist er im Portal auswählbar und '
          + 'bekommt eine eigene Interpreten-Seite — beides dann ohne Bild.\n\n'
          + 'Rückgängig zu machen ist das jederzeit über „Zurückziehen".',
        bestätigenText: 'Trotzdem veröffentlichen',
      });
      if (!trotzdem) return;
    }
    setMutating(true);
    try {
      const updated = await artistService.updateArtist(ar.id, { status });
      setAr(updated);
      notify('success', status === 'PUBLISHED' ? 'Veröffentlicht' : 'Zurückgezogen (Entwurf)');
    } catch {
      notify('error', 'Statuswechsel fehlgeschlagen');
    } finally {
      setMutating(false);
    }
  };

  const handleDelete = async () => {
    if (!ar) return;
    // Stand VOR der Frage festhalten und danach benutzen — gelöscht wird genau der Interpret,
    // dessen Name im Dialog stand (Begründung ausführlich in `AlbumForm.tsx`).
    const { id, name: nameZurZeitDerFrage } = ar;
    // `gefahr: true` — die einzige unwiderrufliche Stelle dieser Datei. Die Folgen sind am
    // Backend nachgelesen (`artistService.deleteArtist`): `content.artistId`, `album.artistId`
    // und `userPlaylist.artistId` laufen auf SetNull, die Anzeige-Spiegel (`content.artist`
    // usw.) bleiben als reiner Text stehen — daher „Name bleibt, Verknüpfung geht". Zusätzlich
    // räumt `deleteLibraryTarget` Merkzeichen und Ordner-Einträge bei ALLEN Nutzern ab.
    const bestätigt = await frage({
      titel: 'Interpret endgültig löschen?',
      text:
        `„${nameZurZeitDerFrage}" wird gelöscht. Das lässt sich nicht rückgängig machen.\n\n`
        + 'Zugeordnete Titel, Alben und öffentliche Playlists bleiben bestehen, verlieren aber '
        + 'die Verknüpfung: der Name bleibt dort als reiner Text stehen, ohne Logo und ohne Link '
        + 'auf eine Interpreten-Seite. Bei allen, die den Interpreten gemerkt haben, verschwindet '
        + 'er aus der Bibliothek.',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      await artistService.deleteArtist(id);
      onDeleted(id);
    } catch {
      notify('error', 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-6">
      {/* Metadaten */}
      <div className="card p-6 space-y-4">
        <div>
          <label htmlFor={`${feldId}-name`} className="block text-sm font-medium text-secondary-700 mb-2">Name *</label>
          <input id={`${feldId}-name`} type="text" value={name} onChange={(e) => setName(e.target.value)} className="input w-full" placeholder="z. B. Energetic Eternity" />
        </div>
        <div>
          <label htmlFor={`${feldId}-bio`} className="block text-sm font-medium text-secondary-700 mb-2">Biografie</label>
          <textarea id={`${feldId}-bio`} value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className="input w-full" placeholder="Kurzer Text über den Interpreten…" />
        </div>
        <div>
          {/* ⚠️ Kein `<label htmlFor>`: unter dieser Überschrift steht KEIN Eingabefeld, sondern
              eine Schaltflächen-Gruppe (aus der Mediathek wählen, entfernen). Richtig ist die
              Gruppe: `role="group"` benennt die Schaltflächen gemeinsam, `aria-labelledby` hängt
              den Text daran. (Der Satz nannte bis 2026-09-08 zusätzlich ein verstecktes
              Datei-Feld — das ist mit dem Upload entfallen.) */}
          <span id={`${feldId}-profilbild`} className="block text-sm font-medium text-secondary-700 mb-2">Profilbild</span>
          <div role="group" aria-labelledby={`${feldId}-profilbild`} className="flex items-center gap-4">
            {profileImageUrl ? (
              <img src={profileImageUrl} alt="Profilbild" className="h-20 w-20 rounded-full object-cover" />
            ) : (
              <div className="h-20 w-20 rounded-full bg-secondary-100 flex items-center justify-center">
                <PhotoIcon className="w-8 h-8 text-secondary-400" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              {/* ⚠️ Hier stand daneben ein „Bild hochladen"-Knopf mit einem versteckten Datei-Feld.
                  Entfallen am 2026-09-08 (Tom-Entscheid: die CMS-Aufrufer der Uploads gehen, der
                  Ingest uebernimmt — `ingestGeteilteBilder.ts` schreibt `artists.profileImageUrl`).
                  Die Mediathek-Auswahl BLEIBT: sie laedt nichts hoch, sie waehlt eine Adresse, die
                  es schon gibt. ⚠️ Folge, die jemand kennen muss: ein Interpret laesst sich erst
                  veroeffentlichen, wenn er ein Bild HAT (`handleStatus` prueft das) — fehlt es und
                  hat der Ingest keines geliefert, ist die Mediathek jetzt der einzige Weg. */}
              <button type="button" onClick={() => setShowImagePicker(true)} className="btn btn-primary">Aus Mediathek wählen</button>
              {profileImageUrl && (
                <button type="button" onClick={() => setProfileImageUrl('')} className="inline-flex items-center text-sm text-error-600 hover:text-error-800">
                  <XMarkIcon className="w-4 h-4 mr-1" /> Bild entfernen
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={handleSaveMeta} disabled={saving} className="btn btn-primary disabled:opacity-50">
            {saving ? 'Speichern…' : isEdit ? 'Änderungen speichern' : 'Interpret anlegen'}
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">{isEdit ? 'Fertig' : 'Abbrechen'}</button>
        </div>
      </div>

      {/* Status + Löschen nur im Edit-Modus */}
      {isEdit && ar && (
        <>
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-secondary-900">Sichtbarkeit</h3>
                <p className="mt-1 text-sm text-secondary-600">
                  {ar.status === 'PUBLISHED' ? (
                    <span className="inline-flex items-center"><span className="badge badge-primary mr-2">Veröffentlicht</span>Im Portal auswählbar + Interpreten-Seite sichtbar.</span>
                  ) : (
                    <span className="inline-flex items-center"><span className="badge badge-secondary mr-2">Entwurf</span>Noch nicht sichtbar — nur hier im CMS.</span>
                  )}
                </p>
              </div>
              {ar.status === 'PUBLISHED' ? (
                <button type="button" onClick={() => setStatus('DRAFT')} disabled={mutating} className="btn btn-outline disabled:opacity-50">Zurückziehen</button>
              ) : (
                <button type="button" onClick={() => setStatus('PUBLISHED')} disabled={mutating} className="btn btn-primary disabled:opacity-50">Veröffentlichen</button>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={handleDelete} className="inline-flex items-center text-sm text-error-600 hover:text-error-800">
              <TrashIcon className="w-4 h-4 mr-1" /> Interpret löschen
            </button>
          </div>
        </>
      )}

      <BunnyFilePicker
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        fileType="image"
        title="Profilbild auswählen"
        onSelect={(url) => {
          setProfileImageUrl(url);
          setShowImagePicker(false);
        }}
      />

      {/* Steht beim Datei-Wähler, dem anderen Overlay dieses Formulars; die Darstellung hängt
          nicht an der Stelle im Baum (Portal an `document.body`). Ohne offene Frage rendert der
          Baustein nichts. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />
    </div>
  );
};
